import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import app, { db } from '../../lib/firebase';
import { getDownloadURL, ref as storageRef, getStorage } from 'firebase/storage';
import cn from 'classnames';
import './admin-details.scss';

type StepEntry = {
  name: string;
  status: string;
  timestamp: Date;
  evidence: string;
  message?: string;
};

type VideoChunkEntry = {
  chunkIndex: number;
  url: string;
  timestamp: Date;
  size: number;
  path?: string;
};

type VideoSessionEntry = {
  sessionId: string;
  startTime: Date;
  endTime?: Date;
  totalDuration: number;
  status: string;
  storageUrls: string[];
  storagePaths?: string[];
  chunkCount: number;
  finalUrl?: string | null;
  finalPath?: string | null;
};

type SOPLogEntry = {
  id: string;
  technicianId: string;
  technicianEmail: string;
  sopName: string;
  startTime: Date;
  endTime?: Date;
  steps: StepEntry[];
  status: 'in-progress' | 'completed' | 'abandoned';
  videoUrl?: string;
  videoSession?: VideoSessionEntry;
  videoChunks?: VideoChunkEntry[];
};

function formatDurationMs(ms: number | undefined) {
  if (!ms || ms < 0) return '—';
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

export const AdminSOPDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [log, setLog] = useState<SOPLogEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [buildingFallback, setBuildingFallback] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState<string | undefined>(undefined);
  const [videoDurationSec, setVideoDurationSec] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [mseUrl, setMseUrl] = useState<string | undefined>(undefined);
  const [mseAttempted, setMseAttempted] = useState<boolean>(false);

  const projectId = (app?.options as any)?.projectId as string | undefined;
  const expectedBucket = projectId ? `${projectId}.appspot.com` : undefined;
  const storageForDownload = useMemo(() => {
    try {
      return expectedBucket ? getStorage(app, `gs://${expectedBucket}`) : getStorage(app);
    } catch {
      return getStorage(app);
    }
  }, [expectedBucket]);

  const normalizeStorageUrl = (u: string): string => {
    try {
      // Rewrite any URL whose /v0/b/<bucket>/o/ bucket part is wrong
      // to use https://firebasestorage.googleapis.com and the expected bucket name.
      const apiHost = 'firebasestorage.googleapis.com';
      const url = new URL(u);
      const path = url.pathname; // e.g. /v0/b/<bucket>/o/<object>
      const match = path.match(/^\/v0\/b\/([^/]+)\/o\/(.*)$/);
      if (match && expectedBucket && match[1] !== expectedBucket) {
        return `https://${apiHost}/v0/b/${expectedBucket}/o/${match[2]}${url.search}`;
      }
      // Ensure we always hit the API hostname (some SDKs can return download URLs via alternate hosts)
      if (url.hostname !== apiHost) {
        if (match) {
          const bucket = match[1];
          const rest = match[2];
          return `https://${apiHost}/v0/b/${bucket}/o/${rest}${url.search}`;
        }
      }
      return u;
    } catch {
      return u;
    }
  };

  // Resolve Storage paths to signed URLs on demand (avoids storing URLs)
  const resolveChunkUrls = async (pathsOrUrls: string[]): Promise<string[]> => {
    const resolved: string[] = [];
    for (const p of pathsOrUrls) {
      // If it looks like a full URL already, normalize and use it
      if (p.startsWith('http')) {
        resolved.push(normalizeStorageUrl(p));
        continue;
      }
      try {
        const sref = storageRef(storageForDownload, p);
        const url = await getDownloadURL(sref);
        resolved.push(normalizeStorageUrl(url));
      } catch (e) {
        // skip failing chunk
      }
    }
    return resolved;
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const snap = await getDoc(doc(db, 'sopLogs', id));
        if (!snap.exists()) {
          throw new Error('SOP log not found');
        }
        const data: any = snap.data();
        const parsed: SOPLogEntry = {
          id: snap.id,
          technicianId: data.technicianId,
          technicianEmail: data.technicianEmail,
          sopName: data.sopName,
          startTime: data.startTime?.toDate?.() || new Date(data.startTime),
          endTime: data.endTime?.toDate?.() || (data.endTime ? new Date(data.endTime) : undefined),
          status: data.status,
          videoUrl: data.videoUrl,
          steps: (data.steps || []).map((s: any) => ({
            name: s.name,
            status: s.status,
            evidence: s.evidence,
            message: s.message,
            timestamp: s.timestamp?.toDate?.() || new Date(s.timestamp)
          })),
          videoSession: data.videoSession ? {
            sessionId: data.videoSession.sessionId,
            startTime: data.videoSession.startTime?.toDate?.() || new Date(data.videoSession.startTime),
            endTime: data.videoSession.endTime?.toDate?.() || (data.videoSession.endTime ? new Date(data.videoSession.endTime) : undefined),
            totalDuration: data.videoSession.totalDuration,
            status: data.videoSession.status,
            storageUrls: data.videoSession.storageUrls || [],
            storagePaths: data.videoSession.storagePaths || [],
            chunkCount: data.videoSession.chunkCount || (data.videoChunks?.length || 0),
            finalUrl: data.videoSession.finalUrl ?? undefined,
            finalPath: data.videoSession.finalPath ?? undefined
          } : undefined,
          videoChunks: (data.videoChunks || []).map((c: any) => ({
            chunkIndex: c.chunkIndex,
            url: c.url,
            path: c.path,
            size: c.size,
            timestamp: c.timestamp?.toDate?.() || new Date(c.timestamp)
          }))
        };
        if (active) setLog(parsed);
      } catch (e: any) {
        if (active) setError(e?.message || 'Failed to load details');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [id]);

  const videoSrc = useMemo(() => {
    if (!log) return undefined;
    return log.videoSession?.finalUrl || log.videoUrl || fallbackUrl;
  }, [log, fallbackUrl]);

  // Prefer MSE streaming URL when available
  const playUrl = useMemo(() => mseUrl || videoSrc, [mseUrl, videoSrc]);

  const statusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('skip')) return '#ff9800';
    if (s.includes('fail')) return '#f44336';
    if (s.includes('pending') || s.includes('miss')) return '#9e9e9e';
    if (s.includes('complete') || s.includes('done') || s.includes('perform')) return '#4caf50';
    return '#607d8b';
  };

  const countsByStatus = useMemo(() => {
    const map = new Map<string, number>();
    (log?.steps || []).forEach(s => {
      const key = (s.status || 'unknown').toLowerCase();
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [log]);

  const uniqueStatuses = useMemo(() => Array.from(countsByStatus.keys()), [countsByStatus]);

  const filteredSteps = useMemo(() => {
    const steps = (log?.steps || []).slice().sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    if (activeFilter === 'all') return steps;
    return steps.filter(s => (s.status || '').toLowerCase() === activeFilter);
  }, [log, activeFilter]);

  const seekToStep = (step: StepEntry) => {
    if (!videoRef.current) return;
    const baseStart = log?.videoSession?.startTime || log?.startTime;
    if (!baseStart) return;
    const startMs = baseStart.getTime();

    // Sort steps by timestamp to determine the previous step boundary
    const stepsSorted = (log?.steps || [])
      .slice()
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    let index = stepsSorted.findIndex((s) => s === step);
    if (index < 0) {
      index = stepsSorted.findIndex(
        (s) => s.timestamp.getTime() === step.timestamp.getTime() && s.name === step.name
      );
    }

    const targetMs = index > 0 ? stepsSorted[index - 1].timestamp.getTime() : startMs;
    const offsetSec = Math.max(0, (targetMs - startMs) / 1000);
    try {
      videoRef.current.currentTime = offsetSec;
      videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      videoRef.current.play().catch(() => {});
    } catch (_) {}
  };

  const stepMarkers = useMemo(() => {
    const startBase = log?.videoSession?.startTime || log?.startTime;
    const endBase = log?.videoSession?.endTime || log?.endTime;
    if (!startBase || !endBase) return [] as { leftPct: number; step: StepEntry }[];
    const start = startBase.getTime();
    const end = endBase.getTime();
    const span = Math.max(1, end - start);
    return (log.steps || [])
      .map(step => ({
        leftPct: Math.max(0, Math.min(100, ((step.timestamp.getTime() - start) / span) * 100)),
        step
      }))
      .sort((a, b) => a.leftPct - b.leftPct);
  }, [log]);

  // Try MediaSource chunk streaming when there is no single-file video
  useEffect(() => {
    const startStreaming = async () => {
      if (!log) return;
      if (log.videoSession?.finalUrl || log.videoUrl) return; // single file exists, no need to stream
      if (!(window as any).MediaSource || !(window as any).MediaSource.isTypeSupported) return;

      const candidate = log.videoSession?.storagePaths?.length
        ? log.videoSession.storagePaths
        : (log.videoChunks || [])
            .slice()
            .sort((a, b) => a.chunkIndex - b.chunkIndex)
            .map(c => c.path || c.url)
            .filter(Boolean) as string[];
      const urls = await resolveChunkUrls(candidate);
      if (!urls || urls.length === 0) return;
      setMseAttempted(true);

      // Probe first chunk to pick an exact mime type
      let firstBuffer: ArrayBuffer | null = null;
      let detectedMime: string | null = null;
      try {
        const headRes = await fetch(normalizeStorageUrl(urls[0]));
        if (!headRes.ok) throw new Error('Failed to fetch first chunk');
        detectedMime = headRes.headers.get('Content-Type');
        const firstBlob = await headRes.blob();
        detectedMime = detectedMime || firstBlob.type || null;
        firstBuffer = await firstBlob.arrayBuffer();
      } catch (_) {
        // If probing fails, let fallback builder handle playback
        return;
      }

      const MediaSourceCls: typeof MediaSource = (window as any).MediaSource;
      const mimeToUse = detectedMime && MediaSourceCls.isTypeSupported(detectedMime)
        ? detectedMime
        : (['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4;codecs=avc1.42E01E', 'video/mp4']
            .find(t => MediaSourceCls.isTypeSupported(t)) || null);
      if (!mimeToUse) {
        return; // unsupported container for MSE, fallback will build Blob
      }

      const mediaSource = new MediaSourceCls();
      const objectUrl = URL.createObjectURL(mediaSource);

      let sourceBuffer: SourceBuffer | null = null;
      let currentIndex = 0;
      let aborted = false;

      const fetchChunk = async (u: string) => {
        const res = await fetch(normalizeStorageUrl(u));
        if (!res.ok) throw new Error('Failed to fetch chunk');
        return await res.arrayBuffer();
      };

      const appendNext = async () => {
        if (!sourceBuffer || aborted) return;
        if (currentIndex >= urls.length) {
          try { mediaSource.endOfStream(); } catch (_) {}
          return;
        }
        try {
          const bufEnd = sourceBuffer.buffered.length ? sourceBuffer.buffered.end(sourceBuffer.buffered.length - 1) : 0;
          // Align next segment after the buffered end (MediaRecorder timeslice usually starts from 0)
          try { (sourceBuffer as any).timestampOffset = bufEnd; } catch (_) {}
          const data = await fetchChunk(urls[currentIndex]);
          if (aborted) return;
          if (sourceBuffer.updating) {
            const onEnd = () => {
              sourceBuffer?.removeEventListener('updateend', onEnd);
              try { sourceBuffer?.appendBuffer(data); } catch (_) {}
            };
            sourceBuffer.addEventListener('updateend', onEnd);
          } else {
            sourceBuffer.appendBuffer(data);
          }
        } catch (e) {
          // On error, stop MSE and fall back to local Blob builder (handled by the next effect)
          aborted = true;
          try { mediaSource.endOfStream(); } catch (_) {}
          URL.revokeObjectURL(objectUrl);
          setMseUrl(undefined);
        }
      };

      const onSourceOpen = () => {
        try {
          sourceBuffer = mediaSource.addSourceBuffer(mimeToUse);
          sourceBuffer.mode = 'segments';
          sourceBuffer.addEventListener('updateend', () => {
            if (aborted) return;
            currentIndex += 1;
            appendNext();
          });
          // First buffer append
          const onFirstEnd = () => {
            sourceBuffer?.removeEventListener('updateend', onFirstEnd);
            if (!aborted) {
              setMseUrl(objectUrl);
              videoRef.current?.load();
              appendNext();
            }
          };
          sourceBuffer.addEventListener('updateend', onFirstEnd);
          sourceBuffer.appendBuffer(firstBuffer!);
        } catch (e) {
          aborted = true;
          try { mediaSource.endOfStream(); } catch (_) {}
          URL.revokeObjectURL(objectUrl);
          setMseUrl(undefined);
        }
      };

      mediaSource.addEventListener('sourceopen', onSourceOpen);

      return () => {
        aborted = true;
        mediaSource.removeEventListener('sourceopen', onSourceOpen);
        try { (sourceBuffer as any)?.abort?.(); } catch (_) {}
        try { mediaSource.endOfStream(); } catch (_) {}
        URL.revokeObjectURL(objectUrl);
        setMseUrl(undefined);
        setMseAttempted(false);
      };
    };

    const cleanupPromise = startStreaming();
    return () => { (async () => { await cleanupPromise; })(); };
  }, [log]);

  // Build a local combined video as a fallback if there is no single-file video
  useEffect(() => {
    const build = async () => {
      if (!log) return;
      const hasSingle = !!(log.videoSession?.finalUrl || log.videoUrl);
      if (mseUrl) return; // already streaming via MSE
      if (hasSingle) {
        setFallbackUrl(undefined);
        return;
      }
      const candidate = log.videoSession?.storagePaths?.length
        ? log.videoSession.storagePaths
        : (log.videoChunks || [])
            .slice()
            .sort((a, b) => a.chunkIndex - b.chunkIndex)
            .map(c => c.path || c.url)
            .filter(Boolean) as string[];
      const urls = await resolveChunkUrls(candidate);
      if (!urls || urls.length === 0) return;
      try {
        setBuildingFallback(true);
        // Fetch chunks sequentially to avoid overwhelming the network
        const blobs: Blob[] = [];
        for (const u of urls) {
          const res = await fetch(u);
          const b = await res.blob();
          blobs.push(b);
        }
        const mimeType = (blobs[0] && blobs[0].type) || 'video/webm';
        const combined = new Blob(blobs, { type: mimeType });
        const objectUrl = URL.createObjectURL(combined);
        setFallbackUrl(objectUrl);
      } catch (e) {
        // leave without fallback
        console.warn('Failed to build fallback combined video', e);
      } finally {
        setBuildingFallback(false);
      }
    };
    build();
    return () => {
      if (fallbackUrl) URL.revokeObjectURL(fallbackUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log?.id, mseUrl]);

  if (loading) return <div className="sop-details loading">Loading details…</div>;
  if (error) return (
    <div className="sop-details">
      <button className="back-btn" onClick={() => navigate(-1)}>{'<'} Back</button>
      <div className="error-text">{error}</div>
    </div>
  );
  if (!log) return null;

  const totalDuration = log.endTime && log.startTime
    ? log.endTime.getTime() - log.startTime.getTime()
    : undefined;

  return (
    <div className="sop-details">
      <div className="topbar">
        <button className="back-btn" onClick={() => navigate('/admin')}>{'<'} Back to Dashboard</button>
      </div>

      <h1 className="title">{log.sopName}</h1>
      <div className="meta">
        <span>{log.technicianEmail}</span>
        <span>•</span>
        <span>{log.startTime.toLocaleString()}</span>
        <span>•</span>
        <span>{formatDurationMs(totalDuration)}</span>
        <span>•</span>
        <span className="status-chip" style={{ background: statusColor(log.status) }}>{log.status}</span>
      </div>

      <div className="summary-grid">
        <div className="summary-card">
          <div className="label">Total Steps</div>
          <div className="value">{log.steps.length}</div>
        </div>
        {uniqueStatuses.map((s) => (
          <div className="summary-card" key={s}>
            <div className="label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="dot" style={{ background: statusColor(s) }} />
              {s}
            </div>
            <div className="value">{countsByStatus.get(s)}</div>
          </div>
        ))}
      </div>

      <div className="yt-layout">
        <div className="player-area">
          {playUrl ? (
            <>
              <video
                ref={videoRef}
                src={playUrl}
                controls
                crossOrigin="anonymous"
                className="video-player"
                onLoadedMetadata={(e) => setVideoDurationSec((e.target as HTMLVideoElement).duration || null)}
              />
              {stepMarkers.length > 0 ? (
                <div className="timeline">
                  {stepMarkers.map((m, i) => (
                    <div
                      key={i}
                      className="marker"
                      title={`${m.step.name} • ${m.step.status}`}
                      style={{ left: `${m.leftPct}%`, background: statusColor(m.step.status) }}
                      onClick={() => seekToStep(m.step)}
                    />
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <div className="video-placeholder">
              No single-file video available.
              {log.videoSession?.storageUrls?.length ? (
                <div className="hint">
                  {`Found ${log.videoSession.storageUrls.length} chunk(s). A combined file may not have been generated.`}
                </div>
              ) : null}
              {buildingFallback ? (
                <div className="hint">Building local preview from chunks…</div>
              ) : null}
            </div>
          )}
        </div>

        <div className="table-area">
          <div className="section-title with-filters">
            <span>Steps</span>
            <div className="filters">
              <button className={cn('filter-chip', { active: activeFilter === 'all' })} onClick={() => setActiveFilter('all')}>All</button>
              {uniqueStatuses.map(s => (
                <button
                  key={s}
                  className={cn('filter-chip', { active: activeFilter === s })}
                  style={{ borderColor: statusColor(s), color: statusColor(s) }}
                  onClick={() => setActiveFilter(s)}
                >
                  {s} ({countsByStatus.get(s)})
                </button>
              ))}
            </div>
          </div>
          <div className="steps-table-wrap">
            <table className="steps-table">
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Message</th>
                  <th>Evidence</th>
                  <th>Status</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {filteredSteps.map((s, idx) => (
                  <tr key={idx} onClick={() => seekToStep(s)} className="step-row">
                    <td className="col-step">{s.name}</td>
                    <td className="col-message">{s.message || '—'}</td>
                    <td className="col-evidence">{s.evidence || '—'}</td>
                    <td>
                      <span className="status-chip" style={{ background: statusColor(s.status) }}>
                        {s.status}
                      </span>
                    </td>
                    <td className="col-time">{s.timestamp.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSOPDetails;

